<template>
  <pl-dialog
    :title="'Import Courses'"
    :size="'lg'"
    :disableOutsideClick="true"
    @close="$emit('close')"
  >
    <div class="px-7 flex-grow overflow-y-auto overflow-x-hidden">
      <div v-if="courses.length" class="my-5 flex items-center">
        <img
          class="w-10 h-10 rounded-full"
          :src="`/static/integrations/${integration.provider_name}.png`"
        />
        <span class="text-base font-semibold text-gray-700 ml-3">
          {{ integration.provider_name }}
        </span>
      </div>
      <div v-if="courses.length" class="mb-2.5 text-sm text-gray-500">
        You can create and roster new courses in Parlay from this window. If you
        want to connect an existing Parlay course to a
        {{ integration.provider_name }} course, you can do this through the
        "Course Settings" tab inside of that course.
      </div>
      <div
        v-if="loadingCourses"
        class="flex items-center justify-center w-full mt-10"
      >
        <pl-spinner class="x2" />
      </div>
      <div
        v-else
        :class="
          courses.length
            ? 'inline-block bg-white rounded-md border w-full text-sm'
            : 'h-full'
        "
      >
        <table v-if="courses.length" class="w-full h-full">
          <thead>
            <th
              class="w-1/12 font-normal border-b text-left p-3 cursor-pointer"
            >
              <pl-checkbox
                v-model="allSelected"
                @change="handleSelectAll($event)"
              />
            </th>
            <th
              class="w-4/12 font-normal border-b text-left p-3 cursor-pointer"
            >
              Title
            </th>
            <th
              class="w-2/12 font-normal border-b text-left p-3 cursor-pointer hidden md:table-cell"
            >
              Subject
            </th>
            <th
              class="w-2/12 font-normal border-b text-left p-3 cursor-pointer hidden md:table-cell"
            >
              Grade
            </th>
            <th
              class="w-2/12 font-normal border-b text-left p-3 cursor-pointer hidden md:table-cell"
            >
              School Site
            </th>
            <th
              class="w-1/12 font-normal border-b text-left p-3 cursor-pointer"
            >
              Existing Parlay Course ?
            </th>
          </thead>
          <tbody name="course-list" is="transition-group" class="">
            <tr
              class="border-b transition-all duration-300"
              v-for="(course, index) in courses"
              :key="course.id"
            >
              <td class="w-1/12 p-3">
                <pl-checkbox
                  v-model="selectedCourses[index]"
                  @change="handleSelectCourse($event, index)"
                  class=""
                />
              </td>
              <td class="w-4/12 p-3 truncate">
                {{ course.name }}
              </td>
              <td class="w-2/12 p-3 truncate hidden md:table-cell">
                <div v-if="course.subjects && course.subjects.length">
                  <div
                    v-for="(subject, index) in course.subjects"
                    :key="index"
                    :title="subject"
                    class="truncate"
                  >
                    {{ subject }}
                  </div>
                </div>
                <div v-else>-</div>
              </td>
              <td class="w-2/12 p-3 truncate hidden md:table-cell">
                <div v-if="course.grades && course.grades.length">
                  <div
                    v-for="(grade, index) in course.grades"
                    :key="index"
                    :title="grade"
                    class="truncate"
                  >
                    {{ grade }}
                  </div>
                </div>
                <div v-else>-</div>
              </td>
              <td class="w-2/12 p-3 truncate hidden md:table-cell">
                <div v-if="course.school_site">
                  {{ course.school_site }}
                </div>
                <div v-else>-</div>
              </td>
              <td class="w-1/12 p-3 truncate">
                <div
                  v-if="course.course_created"
                  class="flex justify-center text-green-600"
                >
                  <i class="fa-solid fa-check text-base leading-4" />
                </div>
                <div v-else class="flex justify-center text-red-600">
                  <i
                    class="fa fa-times text-base leading-4"
                    aria-hidden="true"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div
          v-else
          class="flex flex-col items-center justify-center text-base h-full"
        >
          <div class="mb-5">
            <p>
              You have successfully connected your
              {{ integration.provider_name }} account to Parlay. You can now
              create new Parlay courses directly from your
              {{ integration.provider_name }} account and connect existing
              Parlay courses to existing
              {{ integration.provider_name }} courses.
            </p>
            <p>
              It make a couple of minutes for your
              {{ integration.provider_name }} courses to appear.
            </p>
          </div>
          <button
            @click="goToMyCourses"
            class="bg-primary text-white font-semibold px-8 py-2 rounded"
          >
            GO TO MY COURSES
          </button>
        </div>
      </div>
    </div>
    <footer v-if="courses.length" class="flex items-center justify-center my-5">
      <button
        @click="createCourses"
        class="bg-primary text-white font-semibold px-8 py-2 rounded disabled:opacity-50"
        :disabled="loading || !selected_courses.length"
      >
        <div class="flex">
          <div>CREATE AND ROSTER COURSES</div>
          <div v-show="loading" class="loader" />
        </div>
      </button>
    </footer>
  </pl-dialog>
</template>
<script>
  import { mapGetters } from 'vuex';
  import { EventBus } from 'utils/event-bus';
  export default {
    data() {
      return {
        allSelected: false,
        selectedCourses: [],
        loading: false,
      };
    },
    watch: {
      courses() {
        const is_selected = this.selectedCourses.filter((s) => s === true);
        this.allSelected = is_selected.length === this.courses.length;
      },
    },
    props: {
      courses: {
        type: Array,
        default() {
          return [];
        },
      },
      integration: {
        type: Object,
        default() {
          return {};
        },
      },
      loadingCourses: {
        type: Boolean,
        default() {
          return false;
        },
      },
      memberships: {
        type: Object,
        default() {
          return {};
        },
      },
    },
    computed: {
      ...mapGetters(['getProfile']),
      selected_courses() {
        return this.selectedCourses.filter((s) => s === true);
      },
      is_school_membership() {
        return this.memberships && this.memberships.school_membership;
      },
      valid_membership() {
        if (this.memberships && this.memberships.school_membership) {
          return (
            new Date(this.memberships.school_membership.end_date) > new Date()
          );
        }
        return false;
      },
    },
    methods: {
      handleSelectAll(status) {
        this.selectedCourses = this.courses.map(() => status);
        const is_selected = this.selectedCourses.filter((s) => s === true);
        this.allSelected = is_selected.length === this.courses.length;
      },
      handleSelectCourse(event, index) {
        let boolean_status = false;
        if (event === 'on' || event == true) {
          boolean_status = true;
        } else {
          boolean_status = false;
        }
        if (typeof index === 'number') {
          const status = [...this.selectedCourses];
          status[index] = boolean_status;
          this.selectedCourses = status;
          this.allSelected =
            this.selected_courses.length === this.courses.length;
        }
      },

      goToMyCourses() {
        this.$router.push({
          name: 'my-courses',
        });
      },
      async createCourses() {
        if (!this.is_school_membership) {
          const toast = {
            type: 'Error',
            message: `Your school has no membership.`,
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }
        if (!this.valid_membership) {
          const toast = {
            type: 'Error',
            message: `Your school membership has expired.`,
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }
        this.loading = true;
        const courses_to_import = this.courses.filter(
          (s, index) => this.selectedCourses[index]
        );
        const payload = {
          edlink_courses: courses_to_import,
          school_id: this.getProfile.school.school_id,
        };
        const courses = await this.$store
          .dispatch('createSsoCoursesFromLms', {
            integration_id: this.integration.id,
            payload,
          })
          .catch((err) => {
            console.error(err);
            const toast = {
              type: 'Error',
              message: `Hmm... something went wrong. Please refresh the page or try again.`,
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
            this.loading = false;
          });
        if (courses) {
          const toast = {
            type: 'Success',
            message: `Course(s) Successfully created!`,
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          if (courses.length > 1) {
            this.$router.push({
              name: 'my-courses',
            });
          } else {
            this.$router.push({
              name: 'course.index',
              params: { courseID: courses[0].id },
            });
          }
          this.loading = false;
          this.$emit('close');
        }
      },
    },
  };
</script>
<style scoped lang="stylus">
  table tr:last-child {
    border: none;
  }
  .course-list-enter, .course-list-leave-to
    opacity: 0;
  .course-list-leave-active
    display: none;
</style>



// WEBPACK FOOTER //
// coursesToImportModal.vue?7b8f7f95